import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkle } from "@phosphor-icons/react";
import { Section, ErrorBox, RevisionSelect, SourceTag, errMsg, fmtDate } from "./shared";

export default function AnalyzePanel({ recipeId, recipe, revisions, reviews = [], onReload }) {
  const [revId, setRevId] = useState(recipe.current_revision_id || revisions[0]?.id || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const rev = revisions.find((r) => r.id === revId);

  const run = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post(`/rd/recipes/${recipeId}/analyze`, { revision_id: revId }, { skipNetToast: true });
      setResult({ ...data, revNumber: rev?.number });
      if (onReload) onReload();
    } catch (err) { setError(errMsg(err)); }
    finally { setLoading(false); }
  };

  return (
    <Section icon={Sparkle} title="Contextual AI review" testId="rd-analyze"
      subtitle="Advisory only. Reads the brief and recorded evidence for one revision. It never approves, passes or fails anything.">
      {revisions.length === 0 ? <p className="text-sm text-stone-500">Save a revision to enable review.</p> : (
        <>
          <div className="space-y-2 mb-3">
            <RevisionSelect revisions={revisions} value={revId} onChange={setRevId} testId="select-analyze-revision" />
            <Button onClick={run} disabled={loading || !revId} variant="outline" className="w-full" data-testid="button-run-analysis">
              {loading ? "Reviewing evidence…" : `Review Rev ${rev?.number ?? ""}`}
            </Button>
          </div>
          {loading && <div className="space-y-2 animate-pulse"><div className="h-3 bg-stone-100 rounded" /><div className="h-3 bg-stone-100 rounded w-5/6" /><div className="h-3 bg-stone-100 rounded w-2/3" /></div>}
          <ErrorBox message={error} onRetry={run} testId="error-analyze" />
          {result && (
            <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3" data-testid="text-analysis">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-blue-900 mb-1">Advisory — Rev {result.revNumber} — not an approval</div>
              <div className="text-sm text-stone-800 whitespace-pre-wrap">{result.analysis}</div>
              <div className="mt-3 text-[10px] uppercase tracking-wider font-bold text-stone-500">Sources used</div>
              {(result.sources || []).length === 0 ? <p className="text-xs text-stone-500">No sources were cited.</p> : (
                <ul className="text-xs text-stone-700 list-disc pl-4" data-testid="list-analysis-sources">
                  {result.sources.map((s) => <li key={s.id}><span className="font-mono text-stone-500">{s.id}</span> {s.label}</li>)}
                </ul>
              )}
              {result.disclaimer && <p className="mt-2 text-[11px] text-stone-500 italic">{result.disclaimer}</p>}
            </div>
          )}
          {reviews.length > 0 && (
            <details className="mt-4" data-testid="list-past-reviews">
              <summary className="text-xs text-stone-600 cursor-pointer">Past advisory reviews ({reviews.length})</summary>
              <div className="mt-2 space-y-2">
                {[...reviews].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((r, i) => (
                  <div key={r.id || i} className="rounded border border-stone-200 p-2 text-xs" data-testid={`row-review-${r.id || i}`}>
                    <div className="flex gap-2 items-center flex-wrap mb-1">
                      {r.revision_id && <SourceTag revisions={revisions} revisionId={r.revision_id} currentId={recipe.current_revision_id} />}
                      <span className="text-stone-400">{fmtDate(r.created_at)}</span>
                      <span className="text-[10px] uppercase font-bold text-blue-900">Advisory</span>
                    </div>
                    <div className="text-stone-700 whitespace-pre-wrap">{r.analysis}</div>
                    {(r.sources || []).length > 0 && <div className="text-stone-500 mt-1">Sources: {r.sources.map((s) => s.label || s.id).join("; ")}</div>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </Section>
  );
}
